import { MoneyValueDto } from '@/types/MoneyValueDto.ts';

/**
 * `GET /api/bookings/{bookingId}/bill` — księga cen rezerwacji: żywe wiersze
 * kosztowe wraz z sumami.
 *
 * Sumy jadą razem z liniami, bo liczy je ta sama klasa domeny (`CostLines`),
 * która sumuje rachunek w backendzie — front ich nie składa. Pozycje
 * unieważnione (`voidedAt`) w odpowiedzi nie występują, więc rachunek
 * i podgląd paczki patrzą na identyczny zbiór.
 */

export enum CostLineType {
  BASE = 'BASE',
  OPTION = 'OPTION',
  TRANSPORT = 'TRANSPORT',
  DISCOUNT = 'DISCOUNT',
  MANUAL = 'MANUAL',
}

interface CostLineBase {
  id: string;
  /**
   * `null` znaczy pozycję **całej rezerwacji** i występuje wyłącznie przy
   * `MANUAL`. Projekt nie ustawia `NON_NULL`, więc klucz przychodzi z jawnym
   * `null`, a nie znika z odpowiedzi.
   */
  participantId: string | null;
  amount: MoneyValueDto;
}

/** Cena podstawowa uczestnika; `basePriceName` bywa puste — próg cenowy nie musi mieć nazwy. */
export interface BaseCostLineDto extends CostLineBase {
  type: CostLineType.BASE;
  basePriceName: string | null;
}

/** Opcja wyjazdu: referencje do katalogu plus nazwy z chwili zakupu. */
export interface OptionCostLineDto extends CostLineBase {
  type: CostLineType.OPTION;
  tourOptionId: string;
  optionVariantId: string;
  priceId: string;
  optionName: string;
  variantName: string;
}

export interface TransportCostLineDto extends CostLineBase {
  type: CostLineType.TRANSPORT;
  transportVariantId: string;
  variantName: string;
}

/** Udział rabatu przypadający na uczestnika — kwota zawsze ujemna. */
export interface DiscountCostLineDto extends CostLineBase {
  type: CostLineType.DISCOUNT;
  discountId: string;
  discountCode: string | null;
  discountName: string;
}

/** Pozycja dopisana ręcznie — jedyna bez uczestnika i jedyna o dowolnym znaku kwoty. */
export interface ManualCostLineDto extends CostLineBase {
  type: CostLineType.MANUAL;
  description: string;
}

export type CostLineDto =
  | BaseCostLineDto
  | OptionCostLineDto
  | TransportCostLineDto
  | DiscountCostLineDto
  | ManualCostLineDto;

/**
 * Suma netto grupy rozliczeniowej per waluta. `participantId === null` to grupa
 * całej rezerwacji, czyli pozycje `MANUAL` bez uczestnika.
 */
export interface ParticipantTotalsDto {
  participantId: string | null;
  totals: MoneyValueDto[];
}

export interface BookingBillDto {
  lines: CostLineDto[];
  /** Netto — rabaty są w środku; po jednej pozycji na walutę. */
  totals: MoneyValueDto[];
  totalsByParticipant: ParticipantTotalsDto[];
}

/**
 * Pozycje księgi jednej grupy rozliczeniowej (`null` = cała rezerwacja).
 *
 * Bez filtra waluty: rezerwacja bywa wielowalutowa i wiersz rachunku ma pokazać
 * wszystko, co grupa niesie — 200 zł **i** 333 €, a nie jedno z dwojga.
 */
export const costLinesOf = (
  bill: BookingBillDto,
  participantId: string | null,
): CostLineDto[] =>
  bill.lines.filter((line) => line.participantId === participantId);

/** Sumy grupy rozliczeniowej, po jednej na walutę; pusta lista, gdy grupy nie ma. */
export const totalsOf = (
  bill: BookingBillDto,
  participantId: string | null,
): MoneyValueDto[] =>
  bill.totalsByParticipant.find(
    (entry) => entry.participantId === participantId,
  )?.totals ?? [];
